import React, { useState } from "react";
import { formatUSD } from "../../shared/utils";
import { BulkFilter, isBulk, loadBulkPref, saveBulkPref, AddToListButton, CardLinks } from "../../ui";
import SearchForm from "./SearchForm";

export default function SearchAdd({
  cardName,
  setCardName,
  cardNumber,
  setCardNumber,
  showBulk,
  setShowBulk,
  bulkInput,
  setBulkInput,
  onSearch,
  onBulkAdd,
  searchResults,
  addToCollection,
  hasMore,
  onSeeMore,
  loading = false,
  availableSets = [],
  selectedSets = [],
  onSelectSets = () => {},
  addToWantList,
  isOnWantList,
  isInCollection,
  wantLists = [],
  listNameFor,
  hideOversize = true,
  onHideOversize = () => {},
  oversizeHiddenCount = 0,
  /* Graded prices used to be their own screen you reached by typing the card
     in again. The card is right here; this hands it over rather than asking
     for it a second time. */
  onInspect,
}) {
  const [sort, setSort] = useState("relevance");
  const [bulk, setBulk] = useState(loadBulkPref);
  const changeBulk = (next) => { setBulk(next); saveBulkPref(next); };

  const priceOf = (card) => {
    const p = card?.prices?.["Near Mint"]?.price ?? card?.marketPrice;
    // Guard before Number(): Number(null) is 0, not NaN, so an unpriced card
    // would otherwise look free and lead the low-to-high list.
    if (p === null || p === undefined || p === "") return null;
    const n = Number(p);
    return Number.isFinite(n) ? n : null;
  };

  // Purely a display filter — the credits were spent the moment the results
  // came back. This is about not scrolling past forty dollar cards to reach
  // the two that matter.
  const bulkHiddenCount = React.useMemo(
    () => (searchResults || []).filter((c) => isBulk(priceOf(c), bulk.under)).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchResults, bulk.under]
  );

  const sortedResults = React.useMemo(() => {
    const all = searchResults || [];
    const list = bulk.hide ? all.filter((c) => !isBulk(priceOf(c), bulk.under)) : [...all];
    if (sort === "az") return list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    if (sort === "high" || sort === "low") {
      const dir = sort === "high" ? -1 : 1;
      return list.sort((a, b) => {
        const pa = priceOf(a), pb = priceOf(b);
        // Unpriced cards sink to the bottom either way, rather than pretending
        // to be worth nothing and heading the low-to-high list.
        if (pa === null && pb === null) return 0;
        if (pa === null) return 1;
        if (pb === null) return -1;
        return (pa - pb) * dir;
      });
    }
    return list; // "relevance" keeps whatever order the API returned
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResults, sort, bulk]);

  /* Fetch on until there is something worth looking at.
   *
   * A page is ten cards and the filters are applied after it arrives, so
   * hiding bulk could leave two cards on screen above a See More button —
   * which reads as "that is all there is" when there were fifty more behind
   * it. Pulling the next page automatically fills the grid instead of asking
   * someone to press a button repeatedly to undo their own filter.
   *
   * Capped, because a search where every card is bulk would otherwise walk the
   * whole result set a page at a time. */
  const ENOUGH = 6;
  const MOST_AUTO_PAGES = 4;
  const autoPages = React.useRef(0);
  const seenCount = React.useRef(0);

  React.useEffect(() => {
    // A new search — the results shrank or restarted — starts the budget again.
    if ((searchResults?.length || 0) < seenCount.current) autoPages.current = 0;
    seenCount.current = searchResults?.length || 0;
  }, [searchResults]);

  React.useEffect(() => {
    if (!hasMore || loading) return;
    if (sortedResults.length >= ENOUGH) return;
    if (!searchResults?.length) return;
    if (autoPages.current >= MOST_AUTO_PAGES) return;
    autoPages.current += 1;
    onSeeMore();
  }, [sortedResults.length, hasMore, loading, searchResults, onSeeMore]);

  // Built inside the memo: as a bare const it was a new array every render, so
  // the memo below it never actually memoised anything.
  const setOptions = React.useMemo(() => {
    const normalized = Array.isArray(availableSets)
      ? availableSets.map((s) => (typeof s === "string" ? { id: s, name: s } : s)).filter(Boolean)
      : [];
    return normalized.map((s) => ({ value: s.id, label: s.name }));
  }, [availableSets]);
  const selectedOptions = React.useMemo(
    () => (Array.isArray(selectedSets) ? setOptions.filter((o) => selectedSets.includes(o.value)) : []),
    [setOptions, selectedSets]
  );

  return (
    <>
      <div className="search-bar">
        <SearchForm
          cardName={cardName}
          setCardName={setCardName}
          cardNumber={cardNumber}
          setCardNumber={setCardNumber}
          onSearch={onSearch}
          onToggleBulk={() => setShowBulk(!showBulk)}
          showBulk={showBulk}
          setOptions={setOptions}
          selectedOptions={selectedOptions}
          onSelectSets={onSelectSets}
        />

        <div className="oversize-row">
          <label className="check-inline">
            <input
              type="checkbox"
              checked={hideOversize}
              onChange={(e) => onHideOversize(e.target.checked)}
            />
            Hide oversize (jumbo) cards
          </label>
          {hideOversize && oversizeHiddenCount > 0 && (
            <span className="oversize-note">
              {oversizeHiddenCount} hidden from these results
            </span>
          )}
          <BulkFilter
            hide={bulk.hide}
            under={bulk.under}
            onChange={changeBulk}
            hiddenCount={bulkHiddenCount}
          />
        </div>

        {showBulk && (
          <div className="bulk-panel">
            <textarea
              className="bulk-textarea"
              placeholder="Bulk Add Cards (one per line: Card Name Number)"
              value={bulkInput}
              onChange={(e) => setBulkInput(e.target.value)}
            />
            <button className="btn btn-primary" onClick={onBulkAdd}>Add All</button>
          </div>
        )}
      </div>

      {searchResults?.length > 0 && (
        <>
          <h2 className="section-title">Search Results</h2>

          <div className="sort-row">
            <span className="sort-label">Sort</span>
            {[
              ["relevance", "Relevance"],
              ["az", "A–Z"],
              ["high", "Price: high to low"],
              ["low", "Price: low to high"],
            ].map(([key, label]) => (
              <button
                key={key}
                className={sort === key ? "wl-chip wl-chip--active" : "wl-chip"}
                aria-pressed={sort === key}
                onClick={() => setSort(key)}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Every result being bulk is a real answer, and an empty grid
              under a "Search Results" heading looks like a failure. */}
          {sortedResults.length === 0 ? (
            <p className="msg-empty">
              All {searchResults.length} results are under ${bulk.under}. Lower the bulk
              line or untick it to see them.
            </p>
          ) : (
          <div className="card-grid">
            {sortedResults.map((card) => {
              return (
                <div key={card.id} className="card">
                  <img className="card-image" src={card.imageUrl} alt={card.name} />
                  <div className="card-name">{card.name}</div>
                  <div className="card-number">#{card.number}</div>
                  <div className="card-price">{formatUSD(card.prices["Near Mint"]?.price ?? card.marketPrice)}</div>
                  <CardLinks card={card} game="pokemon" />

                  {/* One label, because it is one destination: that card's
                      page. Naming it after whatever it happens to add — graded
                      prices here, other prints in One Piece — described the
                      contents rather than the door, and gave two names to the
                      same thing depending on which game you were in.

                      Still nothing extra fetched *on* the tile: asking for
                      graded prices on every card in a grid of sixty is sixty
                      calls and buries the buttons that add the card. */}
                  {onInspect && (
                    <button
                      type="button"
                      className="btn btn-link card-inspect"
                      onClick={() => onInspect(card)}
                      title={`View details for ${card.name}`}
                    >
                      View details →
                    </button>
                  )}

                  <div className="card-actions">
                    <button
                      className="btn btn-add"
                      onClick={() => addToCollection(card)}
                      disabled={isInCollection?.(card.id)}
                    >
                      {isInCollection?.(card.id) ? "In Collection" : "Add to Collection"}
                    </button>
                    {addToWantList && (
                      <AddToListButton
                        lists={wantLists}
                        onAdd={(listId) => addToWantList(card, listId)}
                        onList={isOnWantList?.(card.id)}
                        onListName={listNameFor?.(card.id)}
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          )}

          {hasMore && (
            <div style={{ textAlign: "center", marginBottom: 20 }}>
              <button className="btn btn-see-more" onClick={onSeeMore}>See More</button>
            </div>
          )}
        </>
      )}
    </>
  );
}
